
import { customFetcher } from './customFetcher';
import { reservationSchema, reservationStatusSchema, IReservation, IReservationStatus } from '../Interfaces/IReservation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const RESERVATION_QUERY_KEY = ['reservations'];

export const useGetReservation = ({ includeDeleted = false }: { includeDeleted?: boolean } = {}) => {
    const { data: reservationArray, isLoading: isLoadingReservationArray } = useQuery<IReservation[]>({
        refetchOnWindowFocus: false,
        queryKey: [...RESERVATION_QUERY_KEY, includeDeleted],
        queryFn: async () => {
            const data = await customFetcher<unknown[]>({
                endpoint: `/reservations/${includeDeleted ? '?include_deleted=true' : ''}`,
            });
            return reservationSchema.array().parse(data);
        },
        initialData: [],
    });
    return { reservationArray: reservationArray as IReservation[], isLoadingReservationArray };
};

/**
 * Laedt eine einzelne Reservierung ueber den Detail-Endpunkt.
 *
 * Bewusst nicht "im geladenen Array suchen": die Liste haengt an Filtern und am
 * `include_deleted`-Schalter, ein per Deep-Link angesteuerter Datensatz waere dort
 * je nach Ansicht faelschlich "nicht gefunden". `include_deleted=true` wird immer
 * mitgeschickt — das Backend gibt geloeschte Eintraege ohnehin nur Admins und dem
 * Escheck-Team heraus und schraenkt die Abfrage sonst auf die eigenen Buchungen ein.
 */
export const useGetReservationById = (reservationId: number | null) => {
    const { data, isLoading, isFetched, isError } = useQuery<IReservation | null>({
        refetchOnWindowFocus: false,
        queryKey: [...RESERVATION_QUERY_KEY, 'detail', reservationId],
        queryFn: async () => {
            const data = await customFetcher<unknown>({
                endpoint: `/reservations/${reservationId}/?include_deleted=true`,
            });
            return reservationSchema.parse(data);
        },
        enabled: reservationId !== null,
        retry: false,
    });

    return {
        reservation: data ?? null,
        isLoadingReservation: reservationId !== null && isLoading,
        // Erst nach abgeschlossener Anfrage true — nur darauf darf ein
        // "gibt es nicht"-Schluss beruhen (siehe useGetCalendarEntry).
        isFetchedReservation: isFetched,
        isErrorReservation: isError,
    };
};

export const useGetReservationStatus = () => {
    const { data: reservationStatusArray, isLoading: isLoadingReservationStatusArray } = useQuery<IReservationStatus[]>({
        refetchOnWindowFocus: false,
        queryKey: ['reservation_status'],
        queryFn: async () => {
            const data = await customFetcher<unknown[]>({ endpoint: '/reservation-status/' });
            return reservationStatusSchema.array().parse(data);
        },
        initialData: [],
    });
    return { reservationStatusArray: reservationStatusArray as IReservationStatus[], isLoadingReservationStatusArray };
};

export const useCreateReservation = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createReservation } = useMutation<IReservation, Error, IReservation>({
        mutationFn: (newReservation: IReservation) =>
            customFetcher<IReservation>({
                endpoint: `/reservations/`,
                method: 'POST',
                body: newReservation,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: RESERVATION_QUERY_KEY });
        },
    });
    return { createReservation };
};

export const useUpdateReservation = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateReservation, isPending: isUpdatingReservation } = useMutation<IReservation, Error, IReservation>({
        mutationFn: (updatedReservation: IReservation) =>
            customFetcher<IReservation>({
                endpoint: `/reservations/${updatedReservation.id}/`,
                method: 'PUT',
                body: updatedReservation,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: RESERVATION_QUERY_KEY });
        },
    });
    return { updateReservation, isUpdatingReservation };
};

export const useDeleteReservation = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteReservation, isPending: isDeletingReservation } = useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await customFetcher({ endpoint: `/reservations/${id}/`, method: 'DELETE' });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: RESERVATION_QUERY_KEY });
        },
    });
    return { deleteReservation, isDeletingReservation };
};
